import { FC } from "react";

import { Stack } from "@mui/material";
import { getIsoLocaleFromCountry, Utils, Locales } from "@simplyk/common";

import { useLocaleContext } from "../../../../../common/contexts/LocaleContext";
import { useFormV2Context } from "../../contexts/FormV2Context";

import { mergeSx } from "@/common/helpers/mergeSx";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
import { TicketingFieldOutput, DonationFormFieldOutput } from "@/common/types/trpc";
import { FormButtonProps, FormOverlayButton } from "@/forms-design-system/FormButton";
import { FormTypography } from "@/forms-design-system/FormTypography";

export const LanguageSelect: FC = () => {
  const { locale, setIsoLocale } = useLocaleContext();
  const { formObject, formFields } = useFormV2Context();

  const shouldDisplayLanguageSelect = formFields.length > 1;
  const translationLocales = Utils.filterAndMap<TicketingFieldOutput | DonationFormFieldOutput, Locales>(
    formFields,
    (field) => field.locale !== locale,
    (field) => field.locale
  );

  const translateForm = (translationLocale: Locales) => {
    setIsoLocale(getIsoLocaleFromCountry(formObject.organization?.country, translationLocale));
  };

  if (!shouldDisplayLanguageSelect) {
    return null;
  }

  return <StatelessLanguageSelect locales={translationLocales} onClick={translateForm} />;
};

export type StatelessLanguageSelectProps = {
  locales: Locales[];
  onClick: (translationLocale: Locales) => void;
};

export const StatelessLanguageSelect: FC<StatelessLanguageSelectProps> = ({ locales, onClick }) => {
  const { t } = useTranslate();

  return (
    <Stack
      display="inline-flex"
      direction="row"
      gap={1}
      role="group"
      aria-label={t("common", "a11y.language")}
      data-test="form-v2-language-select"
    >
      {locales.map((tLocale) => (
        <LanguagePickerButton
          key={tLocale}
          locale={tLocale}
          onClick={() => onClick(tLocale)}
          data-test="ticketing-toggle-locale"
        />
      ))}
    </Stack>
  );
};

type LanguagePickerButtonProps = FormButtonProps & {
  locale: Locales;
};

const LanguagePickerButton: FC<LanguagePickerButtonProps> = ({ locale, sx, ...rest }) => {
  const isSmallScreen = useMediaQuery();
  const styles = mergeSx(
    {
      padding: 0,
      height: { xs: 28, md: 40 },
      minHeight: { xs: 28, md: 40 },
      minWidth: { xs: 33, md: 52 },
    },
    sx
  );

  return (
    <FormOverlayButton {...rest} lang={locale.toLowerCase()} aria-label={getNativeLanguageName(locale)} sx={styles}>
      <FormTypography variant={isSmallScreen ? "subtitle2" : "subtitle1"}>{locale}</FormTypography>
    </FormOverlayButton>
  );
};

const getNativeLanguageName = (locale: Locales) => {
  const languageTag = locale.toLowerCase();
  const name = new Intl.DisplayNames([languageTag], { type: "language" }).of(languageTag) ?? locale;
  return name.charAt(0).toLocaleUpperCase(languageTag) + name.slice(1);
};
